import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Loader2, Paperclip, Upload, Download, Trash2 } from "lucide-react";
import { format } from "date-fns";

interface Attachment {
  id: string;
  storage_path: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
}

interface Props {
  requestId: string;
  canManage: boolean;
}

export function DSRAttachments({ requestId, canManage }: Props) {
  const { user } = useAuth();
  const [items, setItems] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("dsr_attachments")
      .select("*")
      .eq("request_id", requestId)
      .order("created_at", { ascending: false });
    setItems((data as Attachment[]) ?? []);
    setLoading(false);
  }, [requestId]);

  useEffect(() => { void load(); }, [load]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (file.size > 25 * 1024 * 1024) {
      toast.error("Arquivo muito grande (máx. 25MB)");
      return;
    }

    setUploading(true);
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `${requestId}/${Date.now()}_${safeName}`;

    const { error: upErr } = await supabase.storage
      .from("dsr-evidence")
      .upload(path, file, { contentType: file.type, upsert: false });

    if (upErr) {
      toast.error(`Falha no upload: ${upErr.message}`);
      setUploading(false);
      return;
    }

    const { error: insErr } = await supabase.from("dsr_attachments").insert({
      request_id: requestId,
      storage_path: path,
      filename: file.name,
      mime_type: file.type || null,
      size_bytes: file.size,
      uploaded_by: user.id,
    });

    if (insErr) {
      await supabase.storage.from("dsr-evidence").remove([path]);
      toast.error(`Falha ao registrar: ${insErr.message}`);
    } else {
      toast.success("Arquivo enviado");
      await load();
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleDownload = async (att: Attachment) => {
    const { data, error } = await supabase.storage
      .from("dsr-evidence")
      .createSignedUrl(att.storage_path, 60);
    if (error || !data) {
      toast.error("Falha ao gerar link");
      return;
    }
    window.open(data.signedUrl, "_blank");
  };

  const handleDelete = async (att: Attachment) => {
    if (!confirm(`Remover "${att.filename}"?`)) return;
    await supabase.storage.from("dsr-evidence").remove([att.storage_path]);
    await supabase.from("dsr_attachments").delete().eq("id", att.id);
    toast.success("Removido");
    await load();
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        <Paperclip className="w-4 h-4" /> Anexos da resposta
      </Label>

      {canManage && (
        <div>
          <input
            id={`upl-${requestId}`}
            type="file"
            className="hidden"
            onChange={handleUpload}
            disabled={uploading}
            accept=".pdf,.png,.jpg,.jpeg,.zip,.csv,.json,.txt"
          />
          <Button
            asChild
            size="sm"
            variant="outline"
            disabled={uploading}
          >
            <label htmlFor={`upl-${requestId}`} className="cursor-pointer">
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Anexar arquivo
            </label>
          </Button>
        </div>
      )}

      {loading ? (
        <Skeleton className="h-12 w-full" />
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground">Nenhum anexo.</p>
      ) : (
        <ul className="divide-y border rounded">
          {items.map((a) => (
            <li key={a.id} className="px-2 py-2 flex items-center gap-2 text-sm">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{a.filename}</p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(a.created_at), "dd/MM/yyyy HH:mm")}
                  {a.size_bytes && ` · ${(a.size_bytes / 1024).toFixed(0)} KB`}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => handleDownload(a)}>
                <Download className="w-4 h-4" />
              </Button>
              {canManage && (
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(a)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
